import { useState, useRef, useEffect } from 'react';
import { api } from '../lib/api';
import { WaveformVisualizer } from './WaveformVisualizer';

interface WebcamRecorderProps {
  onComplete: (entryId: string) => void;
}

export function WebcamRecorder({ onComplete }: WebcamRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isFlipped, setIsFlipped] = useState(() => {
    // Load flip preference from localStorage
    const saved = localStorage.getItem('webcam-flip');
    return saved === 'true';
  });
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [analyser, setAnalyser] = useState<AnalyserNode | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const pausedDurationRef = useRef<number>(0);
  const pauseStartRef = useRef<number>(0);
  const isCancelingRef = useRef<boolean>(false);

  // Auto-start camera on page load (Option C: Smart Permission Detection)
  useEffect(() => {
    const autoStartCamera = async () => {
      // Only auto-start if not already previewing
      if (isPreviewing) return;

      try {
        // Check if camera/mic permission already granted
        const cameraResult = await navigator.permissions.query({
          name: 'camera' as PermissionName,
        });
        const micResult = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });

        if (cameraResult.state === 'granted' && micResult.state === 'granted') {
          // Auto-start camera since we already have permission
          await startCamera();
        }
        // Otherwise wait for user to click "Start Camera" button
      } catch (err) {
        // Permissions API not supported (Safari, older browsers)
        // Don't auto-start, wait for user action
        console.log('Permissions API not supported, waiting for user action');
      }
    };

    autoStartCamera();
  }, []); // Only run on mount

  useEffect(() => {
    return () => {
      stopStream();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const toggleFlip = () => {
    const newFlipState = !isFlipped;
    setIsFlipped(newFlipState);
    localStorage.setItem('webcam-flip', String(newFlipState));
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  const setupAudioAnalysis = async (stream: MediaStream) => {
    try {
      const ctx = new AudioContext();
      const analyserNode = ctx.createAnalyser();
      analyserNode.fftSize = 256;

      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyserNode);

      // Ensure AudioContext is running (might be suspended in some browsers)
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      // Update both refs and state
      audioContextRef.current = ctx;
      analyserRef.current = analyserNode;
      setAudioContext(ctx);
      setAnalyser(analyserNode);
    } catch (err) {
      console.error('Failed to setup audio analysis:', err);
    }
  };

  const startCamera = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 },
        audio: true,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      // Setup audio analysis for volume visualization
      await setupAudioAnalysis(stream);

      setIsPreviewing(true);
    } catch (err) {
      setError('Failed to access camera/microphone. Please grant permissions.');
      console.error('Camera access error:', err);
    }
  };

  const startRecording = () => {
    if (!streamRef.current || !videoRef.current || !canvasRef.current) return;

    try {
      chunksRef.current = [];
      setRecordingDuration(0);
      pausedDurationRef.current = 0;

      // If flipped, record from canvas; otherwise record from stream directly
      let recordStream: MediaStream;
      let shouldDrawRef = { current: true }; // Control canvas drawing loop

      if (isFlipped) {
        // Setup canvas to flip video
        const canvas = canvasRef.current;
        const video = videoRef.current;
        canvas.width = 1280;
        canvas.height = 720;
        const ctx = canvas.getContext('2d')!;

        // Draw flipped video to canvas at 30fps
        const drawFrame = () => {
          if (!shouldDrawRef.current) return;
          ctx.save();
          ctx.scale(-1, 1);
          ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height);
          ctx.restore();
          requestAnimationFrame(drawFrame);
        };
        drawFrame();

        // Capture canvas stream + audio from original stream
        const canvasStream = canvas.captureStream(30);
        const audioTrack = streamRef.current.getAudioTracks()[0];
        recordStream = new MediaStream([...canvasStream.getVideoTracks(), audioTrack]);
      } else {
        recordStream = streamRef.current;
      }

      const mediaRecorder = new MediaRecorder(recordStream, {
        mimeType: 'video/webm;codecs=vp9,opus',
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        shouldDrawRef.current = false; // Stop canvas drawing

        // If canceling, don't create blob - just clean up
        if (isCancelingRef.current) {
          isCancelingRef.current = false;
          chunksRef.current = [];
          return;
        }

        const blob = new Blob(chunksRef.current, { type: 'video/webm' });
        setRecordedBlob(blob);
        chunksRef.current = [];

        // Set video to play the recorded blob (keep muted initially)
        if (videoRef.current) {
          const url = URL.createObjectURL(blob);
          videoRef.current.srcObject = null;
          videoRef.current.src = url;
          videoRef.current.muted = true; // Keep muted to prevent auto-playback
        }

        // Stop the live stream after a short delay to ensure blob is fully created
        setTimeout(() => {
          stopStream();
        }, 100);
      };

      mediaRecorder.start(1000); // Capture in 1-second chunks
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setIsPaused(false);

      // Start duration timer
      timerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } catch (err) {
      setError('Failed to start recording.');
      console.error('Recording error:', err);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      pauseStartRef.current = Date.now();

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);

      pausedDurationRef.current += Date.now() - pauseStartRef.current;

      // Restart duration timer
      timerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    }
  };

  const cancelRecording = async () => {
    // Set flag to prevent onstop from creating blob
    isCancelingRef.current = true;

    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
    }

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    chunksRef.current = [];
    setIsRecording(false);
    setIsPaused(false);
    setRecordingDuration(0);
    setRecordedBlob(null);

    // Stop the current stream
    stopStream();

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
      analyserRef.current = null;
      setAudioContext(null);
      setAnalyser(null);
    }

    // Restart the camera to go back to preview state
    await startCamera();
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current = null;
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const uploadToStream = async () => {
    if (!recordedBlob) return;

    try {
      setIsUploading(true);
      setError(null);
      setUploadProgress(0);

      // Get upload URL from backend
      const uploadData = await api.getStreamUploadUrl(recordingDuration, isFlipped);
      const { entryId, uploadURL } = uploadData;

      // Upload to Cloudflare Stream using simple POST (Direct Upload API)
      const formData = new FormData();
      formData.append('file', recordedBlob, `webcam-${Date.now()}.webm`);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentage = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(percentage);
        }
      });

      const uploadPromise = new Promise<void>((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed (${xhr.status}): ${xhr.responseText}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed: Network error'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload aborted'));
        });

        xhr.open('POST', uploadURL);
        xhr.send(formData);
      });

      await uploadPromise;

      // Notify backend that upload is complete
      await api.completeStreamUpload(entryId, recordingDuration);

      setIsUploading(false);
      setUploadProgress(100);

      // Clean up
      stopStream();
      setRecordedBlob(null);
      setIsPreviewing(false);

      // Notify parent
      onComplete(entryId);
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
      setIsUploading(false);
    }
  };

  const discardRecording = async () => {
    setRecordedBlob(null);
    setRecordingDuration(0);

    // Clean up object URL if it exists
    if (videoRef.current && videoRef.current.src) {
      URL.revokeObjectURL(videoRef.current.src);
      videoRef.current.src = '';
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
      analyserRef.current = null;
      setAudioContext(null);
      setAnalyser(null);
    }

    // Restart the camera to go back to preview state
    await startCamera();
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
          {error}
        </div>
      )}

      {/* Waveform Visualizer with instructional text */}
      {isPreviewing && !recordedBlob && (
        <div>
          <p
            className={`text-sm text-gray-500 text-center transition-opacity mb-2 ${
              !isRecording ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ minHeight: '1.25rem' }}
          >
            Speak now to test your microphone volume.
          </p>
          <WaveformVisualizer
            audioContext={audioContext}
            analyser={analyser}
            isActive={isPreviewing}
            isRecording={isRecording && !isPaused}
          />
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden relative">
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="w-full aspect-video bg-gray-900"
          style={{
            transform: isFlipped && !recordedBlob ? 'scaleX(-1)' : 'none',
          }}
        />

        {(isPreviewing || recordedBlob) && (
          <button
            onClick={toggleFlip}
            disabled={isUploading || isRecording}
            className="absolute top-4 left-4 p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg transition-colors disabled:opacity-50"
            title="Flip horizontally"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
              />
            </svg>
          </button>
        )}

        {isRecording && !isPaused && (
          <div className="absolute top-4 right-4 flex items-center space-x-2 bg-red-600 text-white px-3 py-2 rounded-lg">
            <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
            <span className="font-mono">{formatDuration(recordingDuration)}</span>
          </div>
        )}

        {isPaused && (
          <div className="absolute top-4 right-4 flex items-center space-x-2 bg-yellow-600 text-white px-3 py-2 rounded-lg">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="font-mono">{formatDuration(recordingDuration)} (Paused)</span>
          </div>
        )}
      </div>

      {isUploading && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm text-gray-700">
            <span>Uploading...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div
              className="bg-blue-600 h-2 transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {!isPreviewing && !recordedBlob && (
          <button
            onClick={startCamera}
            disabled={isUploading}
            className="px-8 py-4 bg-gray-500 text-white rounded-full hover:bg-gray-600 disabled:bg-gray-400 transition-colors flex items-center space-x-2"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
            </svg>
            <span>Start Camera</span>
          </button>
        )}

        {isPreviewing && !isRecording && !recordedBlob && (
          <button
            onClick={startRecording}
            disabled={isUploading}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 transition-colors flex items-center justify-center space-x-2"
          >
            <div className="w-4 h-4 bg-white rounded-full" />
            <span>Start Recording</span>
          </button>
        )}

        {isRecording && (
          <>
            {/* Pause/Resume Button */}
            <button
              onClick={isPaused ? resumeRecording : pauseRecording}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center space-x-2"
            >
              {isPaused ? (
                <>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                  <span>Resume</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>Pause</span>
                </>
              )}
            </button>

            {/* Stop Button */}
            <button
              onClick={stopRecording}
              className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors flex items-center justify-center space-x-2"
            >
              <div className="w-4 h-4 bg-white" />
              <span>Stop & Save</span>
            </button>

            {/* Cancel Button */}
            <button
              onClick={() => {
                if (confirm('Are you sure you want to discard this recording?')) {
                  cancelRecording();
                }
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Cancel</span>
            </button>
          </>
        )}

        {recordedBlob && !isUploading && (
          <>
            <button
              onClick={uploadToStream}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Upload Video
            </button>
            <button
              onClick={discardRecording}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Discard
            </button>
          </>
        )}
      </div>

      {recordedBlob && !isUploading && (
        <div className="text-sm text-gray-600 text-center">
          Duration: {formatDuration(recordingDuration)}
        </div>
      )}
    </div>
  );
}
